/**
 * P0-SHARE-RECIPIENT-IDENTITY-01 — provider-facing recipient identity
 * projection (presentation only).
 *
 * The provider messaging surfaces (thread list, thread header, secure
 * document-share modal) must all render the SAME identity for a given
 * conversation.
 *
 * P0-MSG-IDENTITY-CONSISTENCY-01 — that identity is now taken from the
 * SERVER-projected thread participant, which is the one surface where the
 * canonical rule (provider-owned → visible, TreatX-sourced → visible only
 * after a verified payment) is evaluated. The lead index is kept only as a
 * fallback: it is a different snapshot of the same person, so preferring it
 * made the list/header disagree with the message bubbles.
 *
 * This module adds NO new identity resolver, performs no fetch, and has no
 * effect on the canonical thread/recipient binding used for submission.
 */

// Internal / pseudonymous participant labels (`Patient-662ae`,
// `user-ab12cd`, …) are storage identifiers. They must never become a
// provider-facing identity just because they exist on the participant row.
export const INTERNAL_PARTICIPANT_LABEL_RE =
  /^(patient|user|person|lead|thread)[-_][0-9a-f]{4,}$/i;

// The server's masked label (`Patient ··0d352`) carries an English word.
// Localise just the word, never the stable reference after it.
const MASKED_LABEL_RE = /^Patient ··(.+)$/;

export const localiseMaskedLabel = (label, patientWord, systemWord) => {
  if (typeof label !== 'string') return label;
  // Bare role words are stored in English on the message/thread doc; the UI
  // must show them in the active language.
  if (/^patient$/i.test(label.trim())) return patientWord || label;
  if (systemWord && /^system$/i.test(label.trim())) return systemWord;
  const m = label.match(MASKED_LABEL_RE);
  if (!m || !patientWord) return label;
  return `${patientWord} ··${m[1]}`;
};

export const resolveThreadPatientLabel = (thread, leadIndex, fallback, patientWord) => {
  if (!thread) return fallback;
  const participant = (thread.participants || []).find((p) => p.role === 'patient');
  const name = participant?.name ? String(participant.name).trim() : '';
  if (name && !INTERNAL_PARTICIPANT_LABEL_RE.test(name)) {
    return localiseMaskedLabel(name, patientWord);
  }
  // UAT-MSG-IDENTITY-SOURCE-AWARE-01 — the lead index is a SECOND projection
  // of the same person and, for a protected TreatX lead, it carries the
  // server's masked value. Falling back to it used to put that masked value
  // (previously a partial name such as "S***") into the inbox, which both
  // leaked a correlatable fragment and disagreed with the thread header.
  // The server projection is now the only identity source; the lead index is
  // used only when the server itself says the identity is NOT masked.
  const leadInfo = thread.context_type === 'lead'
    ? leadIndex?.get?.(thread.context_id)
    : null;
  if (thread.identity_masked !== true && leadInfo?.patient_name && !leadInfo?._pii_masked) {
    return leadInfo.patient_name;
  }
  if (thread.patient_alias) return localiseMaskedLabel(thread.patient_alias, patientWord);
  return fallback;
};
